import { useState, useEffect, useId, useRef } from "react";
import {
  X,
  Calendar,
  ArrowRight,
  ShieldCheck,
  Mail,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  MessageSquare,
  Send,
  Loader2,
} from "lucide-react";
import { useFocusTrap } from "../lib/useFocusTrap";

const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "gmx.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "fastmail.com",
]);

export type BookingModalMode = "review" | "engineering";

export function openBookingModal(mode: BookingModalMode = "review") {
  window.dispatchEvent(new CustomEvent("anter:open-booking", { detail: { mode } }));
}

type BookingModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

export default function BookingModal({
  isOpen: externalIsOpen,
  onClose: externalOnClose,
}: BookingModalProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [mode, setMode] = useState<BookingModalMode>("review");
  const [step, setStep] = useState<"form" | "calendar" | "sent" | "confirmed">("form");

  // Form states
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [dailyRuns, setDailyRuns] = useState("<1k");
  const [useCase, setUseCase] = useState("");
  const [inquiryType, setInquiryType] = useState("VPC & Self-Hosted Deployment");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [serverError, setServerError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState("");
  const [submittedData, setSubmittedData] = useState<{
    email: string;
    company: string;
    dailyRuns?: string;
    useCase?: string;
    message?: string;
    inquiryType?: string;
  } | null>(null);

  const isModalOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const dialogRef = useRef<HTMLDivElement>(null);
  const ids = {
    email: useId(),
    company: useId(),
    dailyRuns: useId(),
    useCase: useId(),
    inquiryType: useId(),
    message: useId(),
    emailError: useId(),
    serverError: useId(),
  };
  useFocusTrap(dialogRef, isModalOpen);

  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  useEffect(() => {
    const handleOpenEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ mode?: BookingModalMode }>;
      const targetMode = customEvent.detail?.mode || "review";
      setMode(targetMode);
      setStep("form");
      setServerError("");
      setInternalIsOpen(true);
    };

    window.addEventListener("anter:open-booking", handleOpenEvent);
    return () => window.removeEventListener("anter:open-booking", handleOpenEvent);
  }, []);

  // Cal.com postMessage listener for booking success
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (!e.data) return;
      const type = typeof e.data === "string" ? e.data : e.data.type || e.data.action;
      if (
        type === "cal:bookingSuccessful" ||
        type === "bookingSuccessful" ||
        type === "cal:bookingCompleted"
      ) {
        console.log("[Booking Modal] Cal.com booking completed successfully:", e.data);
        setStep("confirmed");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  // Close on escape key
  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen]);

  const validateEmail = (inputEmail: string) => {
    const trimmed = inputEmail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setEmailError("Please enter a valid work email address.");
      return false;
    }
    const domain = trimmed.split("@")[1];
    if (domain && PERSONAL_EMAIL_DOMAINS.has(domain)) {
      setEmailError("Please use your work/business email address (e.g. name@company.com).");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) return;
    if (!company.trim()) return;

    setIsSubmitting(true);
    setServerError("");

    const leadInfo = { email, company, dailyRuns, useCase };
    setSubmittedData(leadInfo);

    try {
      const res = await fetch("/api/inbound/architecture-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(leadInfo),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setServerError(data.message || "Failed to submit lead data.");
        setIsSubmitting(false);
        return;
      }

      setCalendarUrl(
        data.calendarUrl ||
          `https://cal.com/marvin-perez-oskixz/30min?embed=true&email=${encodeURIComponent(
            email
          )}&name=${encodeURIComponent(company)}`
      );
      setStep("calendar");
    } catch (err) {
      console.warn("Backend API dispatch failed, proceeding with fallback calendar embed:", err);
      // Fallback url
      setCalendarUrl(
        `https://cal.com/marvin-perez-oskixz/30min?embed=true&email=${encodeURIComponent(
          email
        )}&name=${encodeURIComponent(company)}`
      );
      setStep("calendar");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEngineering = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) return;
    if (!company.trim()) return;
    if (!message.trim()) return;

    setIsSubmitting(true);
    setServerError("");

    const inquiryInfo = { email, company, inquiryType, message };
    setSubmittedData(inquiryInfo);

    try {
      const res = await fetch("/api/inbound/contact-engineering", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(inquiryInfo),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setServerError(data.message || "Failed to send inquiry.");
        setIsSubmitting(false);
        return;
      }

      setStep("sent");
    } catch (err) {
      console.warn("Backend API dispatch failed, displaying fallback confirmation:", err);
      setStep("sent");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-3 sm:p-4 md:p-6"
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-modal-title"
        aria-describedby={serverError ? ids.serverError : undefined}
        className="relative flex w-full max-w-2xl max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-bg-elevated text-text shadow-2xl transition-all animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header — Fixed Top */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-bg-secondary/80 shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-primary-border bg-primary-dim text-primary">
              {mode === "review" ? (
                <Calendar className="h-4 w-4" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
            </span>
            <div>
              <h2 id="booking-modal-title" className="text-base font-bold text-text sm:text-lg">
                {mode === "review" ? "Book Architecture Review" : "Talk to Anter Engineering"}
              </h2>
              <p className="text-xs text-text-muted">
                {mode === "review"
                  ? "Direct 30-min live calendar scheduling with senior engineers"
                  : "Send a technical inquiry directly to our core architecture team"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close modal"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-text-faint transition hover:bg-bg-hover hover:text-text cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mode Switcher Tabs — Fixed Top */}
        <div
          role="tablist"
          aria-label="Contact method"
          className="flex border-b border-border bg-bg-secondary/40 px-6 py-2 shrink-0"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "review"}
            onClick={() => {
              setMode("review");
              setStep("form");
              setServerError("");
            }}
            className={`flex-1 min-h-11 py-1.5 text-xs font-semibold text-center rounded-lg transition cursor-pointer ${
              mode === "review"
                ? "bg-bg border border-primary-border text-primary-text"
                : "text-text-muted hover:text-text"
            }`}
          >
            Schedule Live Call (30 min)
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "engineering"}
            onClick={() => {
              setMode("engineering");
              setStep("form");
              setServerError("");
            }}
            className={`flex-1 min-h-11 py-1.5 text-xs font-semibold text-center rounded-lg transition cursor-pointer ${
              mode === "engineering"
                ? "bg-bg border border-primary-border text-primary-text"
                : "text-text-muted hover:text-text"
            }`}
          >
            Send Technical Message
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {serverError && (
            <div
              id={ids.serverError}
              role="alert"
              className="rounded-xl border border-red-dim bg-red-dim/20 p-3.5 flex items-center gap-2 text-xs text-red"
            >
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{serverError}</span>
            </div>
          )}

          {/* MODE 1: ARCHITECTURE REVIEW (FORM) */}
          {mode === "review" && step === "form" && (
            <form id="review-form" onSubmit={handleSubmitReview} className="space-y-4">
              <div className="rounded-xl border border-primary-border/60 bg-primary-dim/40 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-text-muted leading-relaxed">
                    Schedule a 30-minute system design review with an Anter senior infrastructure
                    engineer. Fill out your details below to unlock our live calendar.
                  </p>
                </div>
              </div>

              {/* Work Email */}
              <div>
                <label htmlFor={ids.email} className="block text-xs font-semibold text-text mb-1.5">
                  Work Email{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id={ids.email}
                  type="email"
                  required
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? ids.emailError : undefined}
                  placeholder="alex@acme.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  className={`w-full rounded-xl border ${
                    emailError ? "border-red" : "border-border"
                  } bg-bg-secondary px-4 py-2.5 text-sm text-text placeholder:text-text-faint focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
                />
                {emailError && (
                  <p
                    id={ids.emailError}
                    role="alert"
                    className="mt-1.5 flex items-center gap-1.5 text-xs text-red"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {emailError}
                  </p>
                )}
              </div>

              {/* Company Name & Team Size */}
              <div>
                <label
                  htmlFor={ids.company}
                  className="block text-xs font-semibold text-text mb-1.5"
                >
                  Company Name / Team Size{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id={ids.company}
                  type="text"
                  required
                  autoComplete="organization"
                  aria-required="true"
                  placeholder="Acme Corp — 15 Engineers"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-2.5 text-sm text-text placeholder:text-text-faint focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>

              {/* Estimated Daily Agent Runs */}
              <div>
                <label
                  htmlFor={ids.dailyRuns}
                  className="block text-xs font-semibold text-text mb-1.5"
                >
                  Estimated Daily Agent Runs{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <select
                  id={ids.dailyRuns}
                  value={dailyRuns}
                  onChange={(e) => setDailyRuns(e.target.value)}
                  aria-required="true"
                  className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="<1k">&lt;1k</option>
                  <option value="1k–10k">1k–10k</option>
                  <option value="10k–100k">10k–100k</option>
                  <option value="100k+">100k+</option>
                </select>
              </div>

              {/* Primary Use Case / Integration Need */}
              <div>
                <label
                  htmlFor={ids.useCase}
                  className="block text-xs font-semibold text-text mb-1.5"
                >
                  Primary Use Case / Integration Need{" "}
                  <span className="text-text-faint font-normal">(Optional)</span>
                </label>
                <textarea
                  id={ids.useCase}
                  rows={3}
                  placeholder="Describe your target workflows, tool integrations, or custom deployment requirements..."
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-2.5 text-sm text-text placeholder:text-text-faint focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </form>
          )}

          {/* MODE 1 STEP 2: CALENDAR EMBED */}
          {mode === "review" && step === "calendar" && (
            <div className="space-y-5">
              <div className="flex items-center justify-between rounded-xl border border-primary-border bg-primary-dim px-4 py-3">
                <div className="flex items-center gap-2 text-xs font-medium text-text">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  <span>
                    Intent qualified & logged for{" "}
                    <strong className="text-primary-text">{submittedData?.company}</strong>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setStep("form")}
                  className="text-xs text-text-muted underline hover:text-text cursor-pointer"
                >
                  Edit info
                </button>
              </div>

              <div className="relative min-h-[380px] w-full overflow-hidden rounded-xl border border-border bg-bg">
                <iframe
                  src={
                    calendarUrl ||
                    `https://cal.com/marvin-perez-oskixz/30min?embed=true&email=${encodeURIComponent(
                      submittedData?.email || ""
                    )}&name=${encodeURIComponent(submittedData?.company || "")}`
                  }
                  title="Schedule Architecture Review with Anter Engineering"
                  className="h-[420px] w-full border-0"
                />
              </div>

              <div className="rounded-xl border border-border bg-bg-secondary p-4 space-y-3">
                <p className="text-xs font-semibold text-text">
                  Having trouble loading the calendar widget?
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="https://cal.com/marvin-perez-oskixz/30min"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-bg px-3 py-1.5 text-xs font-semibold text-text transition hover:border-primary-border hover:text-primary-text"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open Cal.com in new tab
                  </a>
                  <a
                    href={`mailto:engineering@anter.ai?subject=Architecture%20Review%20Request%20-%20${encodeURIComponent(
                      submittedData?.company || ""
                    )}&body=${encodeURIComponent(
                      `Email: ${submittedData?.email}\nCompany: ${submittedData?.company}\nDaily Runs: ${submittedData?.dailyRuns}\nUse Case: ${submittedData?.useCase}`
                    )}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-bg px-3 py-1.5 text-xs font-semibold text-text transition hover:border-primary-border hover:text-primary-text"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Email engineering@anter.ai
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* MODE 1 STEP 3: BOOKING CONFIRMED SCREEN */}
          {step === "confirmed" && (
            <div className="space-y-6 text-center py-6">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-primary-border bg-primary-dim text-primary">
                <CheckCircle2 className="h-9 w-9 text-primary animate-bounce" />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-text">
                  Architecture Review Confirmed!
                </h3>
                <p className="mt-2 text-sm text-text-muted max-w-md mx-auto leading-relaxed">
                  We look forward to meeting with{" "}
                  <strong className="text-text">{submittedData?.company}</strong>. Calendar
                  invitation and Google Meet details have been sent to{" "}
                  <strong className="text-primary-text">{submittedData?.email}</strong>.
                </p>
              </div>

              <div className="rounded-xl border border-primary-border bg-primary-dim/30 p-4 max-w-md mx-auto text-left space-y-2 text-xs">
                <p className="font-bold text-text">Session Details:</p>
                <p>
                  <span className="text-text-faint">Host:</span> Anter Core Infrastructure
                  Engineering
                </p>
                <p>
                  <span className="text-text-faint">Format:</span> 30-min Video Call (Google Meet)
                </p>
                <p>
                  <span className="text-text-faint">Attendee:</span> {submittedData?.email}
                </p>
              </div>
            </div>
          )}

          {/* MODE 2: TALK TO ENGINEERING (FORM) */}
          {mode === "engineering" && step === "form" && (
            <form id="engineering-form" onSubmit={handleSubmitEngineering} className="space-y-4">
              <div className="rounded-xl border border-primary-border/60 bg-primary-dim/40 p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-text-muted leading-relaxed">
                    Have a technical question about custom VPC setup, security questionnaires, or
                    tool connectors? Send a message directly to our core engineering team.
                  </p>
                </div>
              </div>

              {/* Work Email */}
              <div>
                <label htmlFor={ids.email} className="block text-xs font-semibold text-text mb-1.5">
                  Work Email{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id={ids.email}
                  type="email"
                  required
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? ids.emailError : undefined}
                  placeholder="alex@acme.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  className={`w-full rounded-xl border ${
                    emailError ? "border-red" : "border-border"
                  } bg-bg-secondary px-4 py-2.5 text-sm text-text placeholder:text-text-faint focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
                />
                {emailError && (
                  <p
                    id={ids.emailError}
                    role="alert"
                    className="mt-1.5 flex items-center gap-1.5 text-xs text-red"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {emailError}
                  </p>
                )}
              </div>

              {/* Company & Role */}
              <div>
                <label
                  htmlFor={ids.company}
                  className="block text-xs font-semibold text-text mb-1.5"
                >
                  Company Name & Role{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <input
                  id={ids.company}
                  type="text"
                  required
                  autoComplete="organization"
                  aria-required="true"
                  placeholder="Acme Corp — Lead AI Architect"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-2.5 text-sm text-text placeholder:text-text-faint focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>

              {/* Inquiry Topic */}
              <div>
                <label
                  htmlFor={ids.inquiryType}
                  className="block text-xs font-semibold text-text mb-1.5"
                >
                  Inquiry Topic{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <select
                  id={ids.inquiryType}
                  value={inquiryType}
                  onChange={(e) => setInquiryType(e.target.value)}
                  aria-required="true"
                  className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-2.5 text-sm text-text focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <option value="VPC & Self-Hosted Deployment">VPC & Self-Hosted Deployment</option>
                  <option value="Custom Tool / MCP Integrations">
                    Custom Tool / MCP Integrations
                  </option>
                  <option value="Security & SOC 2 Questionnaire">
                    Security & SOC 2 Questionnaire
                  </option>
                  <option value="General Technical Inquiry">General Technical Inquiry</option>
                </select>
              </div>

              {/* Technical Message */}
              <div>
                <label
                  htmlFor={ids.message}
                  className="block text-xs font-semibold text-text mb-1.5"
                >
                  Technical Question / Details{" "}
                  <span className="text-primary" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(required)</span>
                </label>
                <textarea
                  id={ids.message}
                  rows={4}
                  required
                  aria-required="true"
                  placeholder="Ask us anything about our infrastructure, MCP servers, evaluation framework, or deployment architecture..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg-secondary px-4 py-2.5 text-sm text-text placeholder:text-text-faint focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </form>
          )}

          {/* MODE 2 STEP 2: MESSAGE SENT CONFIRMATION */}
          {mode === "engineering" && step === "sent" && (
            <div className="space-y-6 text-center py-4">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary-border bg-primary-dim text-primary">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-text">Inquiry Sent to Engineering</h3>
                <p className="mt-2 text-sm text-text-muted max-w-md mx-auto leading-relaxed">
                  Thank you, <strong className="text-text">{submittedData?.email}</strong>. Our core
                  infrastructure engineers will review your question and respond within 2 hours.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-bg-secondary p-4 max-w-md mx-auto text-left space-y-2 text-xs">
                <p className="font-semibold text-text">Inquiry Summary:</p>
                <p>
                  <span className="text-text-faint">Topic:</span> {submittedData?.inquiryType}
                </p>
                <p>
                  <span className="text-text-faint">Company:</span> {submittedData?.company}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* STICKY FOOTER ACTIONS */}
        {step === "form" && (
          <div className="border-t border-border bg-bg-secondary/95 backdrop-blur-md px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
            {mode === "review" ? (
              <>
                <button
                  type="submit"
                  form="review-form"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-primary bg-primary px-6 py-2.5 text-xs font-bold text-primary-text-strong transition hover:opacity-90 cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Validating & Logging Lead...
                    </>
                  ) : (
                    <>
                      Unlock Live Calendar
                      <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("engineering");
                    setServerError("");
                  }}
                  className="text-xs text-text-muted hover:text-primary-text transition flex items-center gap-1.5 cursor-pointer"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  Have a quick question? Send a message
                </button>
              </>
            ) : (
              <>
                <button
                  type="submit"
                  form="engineering-form"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-primary bg-primary px-6 py-2.5 text-xs font-bold text-primary-text-strong transition hover:opacity-90 cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Dispatching Message...
                    </>
                  ) : (
                    <>
                      Send Message to Engineering
                      <Send className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("review");
                    setServerError("");
                  }}
                  className="text-xs text-text-muted hover:text-primary-text transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Calendar className="h-3.5 w-3.5" />
                  Prefer a live call? Schedule a slot
                </button>
              </>
            )}
          </div>
        )}

        {/* STICKY FOOTER FOR CONFIRMED / SENT / CALENDAR MODES */}
        {step !== "form" && (
          <div className="border-t border-border bg-bg-secondary/95 backdrop-blur-md px-6 py-3.5 flex items-center justify-end gap-3 shrink-0">
            {mode === "engineering" && step === "sent" && (
              <button
                type="button"
                onClick={() => {
                  setMode("review");
                  setStep("calendar");
                }}
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-5 py-2 text-xs font-bold text-primary-text-strong transition hover:opacity-90 cursor-pointer"
              >
                Schedule Live Call as well
                <Calendar className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-bg px-4 py-2 text-xs font-semibold text-text transition hover:bg-bg-hover cursor-pointer"
            >
              Close Window
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
